/**
 * Project picker rendered inside the agent panel whenever a project
 * hasn't been chosen yet (or the user asks to switch from the
 * sidebar).
 *
 * The hero header ("Open a project.") + subtitle live inside the
 * shared :class:`DirectoryBrowser`, so this component is a thin
 * adapter that wires Antioch-specific bits (session-count badges,
 * "+ Folder" creation, the "Open project" CTA) onto the generic
 * file-tree surface. Any directory on disk can be chosen as a
 * project — folders the user has chatted in before are badged
 * with their session count.
 *
 * Emits ``onOpen(cwd)`` on commit. Session selection lives in
 * :class:`SessionSidebar` next to the chat — the picker is
 * project-only and the overlay falls back to the most-recent session
 * for that cwd via ``fetchSessions`` after the pick.
 */

import { Folder, FolderOpen, Plus, X } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
  type BrowserNavigationDirection,
  DirectoryBrowser,
  DirectoryBrowserRow,
} from '@/components/FileBrowser/DirectoryBrowser';
import { FolderIcon } from '@/components/FileBrowser/icons';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { SimpleTooltip } from '@/components/ui/tooltip';
import type { AgentDirectoryListing } from '@/lib/agent/api';
import { createDirectory, fetchDirectoryEntries } from '@/lib/agent/api';
import { cn } from '@/lib/utils/cn';
import { formatRelativeFromEpochMs } from '@/lib/utils/date';
import { AgentLoadingState } from './AgentLoadingState';

export interface ProjectPickerProps {
  /** Fires when the user finalizes a project pick. */
  onOpen: (cwd: string) => void;
  /** Optional directory to show first instead of the workspace home. */
  initialPath?: string | null;
  className?: string;
}

export function ProjectPicker({ onOpen, initialPath, className }: ProjectPickerProps) {
  const [listing, setListing] = React.useState<AgentDirectoryListing | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [navDirection, setNavDirection] = React.useState<BrowserNavigationDirection>('forward');
  const [isCreatingFolder, setIsCreatingFolder] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [creatingBusy, setCreatingBusy] = React.useState(false);
  const newFolderInputRef = React.useRef<HTMLInputElement>(null);

  const load = React.useCallback(async (path: string | undefined, direction: BrowserNavigationDirection) => {
    setLoading(true);
    setError(null);
    setNavDirection(direction);
    try {
      const next = await fetchDirectoryEntries(path);
      setListing(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load(initialPath ?? undefined, 'forward');
  }, [initialPath, load]);

  React.useEffect(() => {
    if (!isCreatingFolder) return;
    setNewFolderName('');
    requestAnimationFrame(() => newFolderInputRef.current?.focus());
  }, [isCreatingFolder]);

  const navigateTo = (path: string, direction: 'forward' | 'back' = 'forward') => {
    setIsCreatingFolder(false);
    setNewFolderName('');
    load(path, direction);
  };

  // The workspace anchor (``home``) is the top of the agent picker —
  // users can browse into sub-folders but shouldn't escape above it
  // via the breadcrumb or the up arrow.
  const atWorkspaceRoot = listing != null && listing.path === listing.home;
  const exposedParent = atWorkspaceRoot ? null : (listing?.parent ?? null);

  const navigateUp = () => {
    if (exposedParent) navigateTo(exposedParent, 'back');
  };

  const cancelCreating = () => {
    setIsCreatingFolder(false);
    setNewFolderName('');
  };

  const confirmCreate = async () => {
    if (!listing) return;
    const trimmed = newFolderName.trim();
    if (!trimmed) {
      toast.error('Please enter a folder name');
      return;
    }
    if (/[/\\]/.test(trimmed) || trimmed === '.' || trimmed === '..') {
      toast.error('Folder name contains invalid characters');
      return;
    }

    setCreatingBusy(true);
    try {
      const created = await createDirectory(listing.path, trimmed);
      toast.success(`Created ${created.name}`);
      setIsCreatingFolder(false);
      setNewFolderName('');
      // Drop the user inside the new folder so the primary action
      // ("Open project") immediately commits to using it.
      load(created.path, 'forward');
    } catch (err) {
      toast.error(`Failed to create folder: ${err instanceof Error ? err.message : 'unknown error'}`);
    } finally {
      setCreatingBusy(false);
    }
  };

  if (loading && !listing && !error) {
    return <ProjectPickerLoading className={className} />;
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-1 flex-col overflow-hidden pb-5', className)}>
      <DirectoryBrowser
        title="Open a project."
        description="Pick any project folder on disk for your agent session. Folders you've chatted in before are badged with their session count."
        currentPath={listing?.path ?? ''}
        parentPath={exposedParent}
        homePath={listing?.home ?? null}
        loading={loading && !listing}
        error={error}
        navDirection={navDirection}
        headerActions={
          <SimpleTooltip content="New folder" side="bottom">
            <Button
              variant="outline"
              size="sm"
              disabled={!listing}
              className={cn(
                'h-8 select-none gap-1.5 px-3 text-[12.5px]',
                isCreatingFolder &&
                  'border-gray-300 bg-gray-50 text-gray-900 dark:border-white/15 dark:bg-white/[0.06] dark:text-gray-100'
              )}
              onClick={() => setIsCreatingFolder((prev) => !prev)}
            >
              <Plus className="size-3.5" />
              Folder
            </Button>
          </SimpleTooltip>
        }
        primaryAction={{
          label: formatChooseLabel(listing?.session_count ?? 0),
          disabled: !listing,
          variant: 'outline',
          icon: <FolderOpen className="size-3.5" />,
          // Tint the CTA when the current folder already has sessions
          // so it's obvious the click resumes an existing project.
          className:
            listing?.session_count && listing.session_count > 0
              ? 'border-gray-300 bg-gray-50 text-gray-900 hover:bg-gray-100 dark:border-white/15 dark:bg-white/[0.06] dark:text-gray-100 dark:hover:bg-white/10'
              : undefined,
          onClick: () => listing && onOpen(listing.path),
        }}
        onNavigate={(path) => navigateTo(path, 'back')}
        onNavigateUp={navigateUp}
        onRetry={() => load(listing?.path, 'forward')}
      >
        {isCreatingFolder && (
          <div className="mb-1 flex items-center gap-2.5 px-4 py-1.5">
            <Folder className="size-4 shrink-0 text-gray-500 dark:text-gray-400" />
            <input
              ref={newFolderInputRef}
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  confirmCreate();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelCreating();
                }
              }}
              placeholder="folder_name"
              className="h-8 min-w-0 flex-1 select-text rounded-md border border-gray-200 bg-white/90 px-2.5 text-[13px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-gray-400 dark:border-white/10 dark:bg-gray-950/50 dark:text-gray-100 dark:focus:border-gray-500 dark:placeholder:text-gray-500"
              disabled={creatingBusy}
            />
            {creatingBusy ? (
              <Spinner size="size-3.5" className="text-gray-500 dark:text-gray-400" />
            ) : (
              <>
                <button
                  type="button"
                  className="h-7 shrink-0 cursor-pointer select-none rounded-md px-2.5 font-medium text-[12px] text-gray-700 transition-colors hover:bg-gray-200/70 dark:text-gray-300 dark:hover:bg-white/10"
                  onClick={confirmCreate}
                >
                  Create
                </button>
                <button
                  type="button"
                  aria-label="Cancel"
                  className="flex size-7 shrink-0 cursor-pointer select-none items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-200"
                  onClick={cancelCreating}
                >
                  <X className="size-3.5" />
                </button>
              </>
            )}
          </div>
        )}
        {!isCreatingFolder && listing?.entries.length === 0 && (
          <EmptyState
            icon={FolderOpen}
            title="No sub-folders"
            description={'Use "Open project" to pick the current path, or create a new one above.'}
          />
        )}
        {listing?.entries.map((entry) => (
          <DirectoryBrowserRow
            key={entry.path}
            icon={<FolderIcon />}
            label={entry.name}
            meta={formatRelativeFromEpochSeconds(entry.last_modified)}
            badge={
              entry.session_count && entry.session_count > 0 ? (
                <SimpleTooltip content={`${formatSessionCount(entry.session_count)} for this folder`} side="bottom">
                  <span className="shrink-0 select-none rounded-full bg-gray-100 px-1.5 py-0.5 font-medium text-[10px] text-gray-600 dark:bg-white/10 dark:text-gray-300">
                    {formatSessionCount(entry.session_count)}
                  </span>
                </SimpleTooltip>
              ) : undefined
            }
            disclosure
            title={`Click to open · double-click to use ${entry.name}`}
            onClick={() => navigateTo(entry.path, 'forward')}
            onDoubleClick={() => listing && onOpen(entry.path)}
          />
        ))}
      </DirectoryBrowser>
    </div>
  );
}

function ProjectPickerLoading({ className }: { className?: string }) {
  return <AgentLoadingState title="Loading projects" description="Scanning workspace folders" className={className} />;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatRelativeFromEpochSeconds(epochSeconds: number | null | undefined): string | null {
  if (epochSeconds == null || !Number.isFinite(epochSeconds)) return null;
  return formatRelativeFromEpochMs(epochSeconds * 1000);
}

function formatSessionCount(count: number): string {
  return `${count} ${count === 1 ? 'session' : 'sessions'}`;
}

/**
 * Build the primary CTA label. When the user is standing in a folder
 * that already has persisted sessions, surface that on the button so
 * it's obvious "Choose" will drop them into existing history rather
 * than start from scratch.
 */
function formatChooseLabel(currentSessionCount: number): string {
  if (currentSessionCount > 0) {
    return `Open project · ${formatSessionCount(currentSessionCount)}`;
  }
  return 'Open project';
}
